import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Mail, TrendingUp, AlertCircle, Check, X, Eye, EyeOff, Send } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface SMTPAccount {
  _id: string;
  email: string;
  smtpUsername?: string;
  dailyLimit: number;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  fromName: string;
  fromEmail?: string;
  replyToEmail?: string;
  isActive: boolean;
  priority: number;
  todayUsage: number;
  remainingToday: number;
  totalEmailsSent: number;
  lastUsedAt?: string;
  createdAt: string;
}

export default function AdminSMTPAccounts() {
  const [accounts, setAccounts] = useState<SMTPAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SMTPAccount | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingAccount, setTestingAccount] = useState<SMTPAccount | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  
  const [formData, setFormData] = useState({
    smtpProvider: 'custom',
    email: '',
    smtpUsername: '',
    password: '',
    dailyLimit: 500,
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    fromName: 'Atootbandan',
    fromEmail: '',
    replyToEmail: '',
    priority: 1,
    isActive: true,
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/admin/smtp`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (data.success) {
        setAccounts(data.data.accounts);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch SMTP accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingAccount) {
        const { data } = await axios.put(
          `${API_URL}/admin/smtp/${editingAccount._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (data.success) {
          toast.success('SMTP account updated successfully');
          fetchAccounts();
          handleCloseModal();
        }
      } else {
        const { data } = await axios.post(
          `${API_URL}/admin/smtp`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (data.success) {
          toast.success('SMTP account added successfully');
          fetchAccounts();
          handleCloseModal();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this SMTP account?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.delete(`${API_URL}/admin/smtp/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (data.success) {
        toast.success('SMTP account deleted successfully');
        fetchAccounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
    }
  };

  const handleEdit = (account: SMTPAccount) => {
    setEditingAccount(account);
    setFormData({
      smtpProvider: 'custom',
      email: account.email,
      smtpUsername: account.smtpUsername || '',
      password: '',
      dailyLimit: account.dailyLimit,
      smtpHost: account.smtpHost,
      smtpPort: account.smtpPort,
      smtpSecure: account.smtpSecure,
      fromName: account.fromName,
      fromEmail: account.fromEmail || '',
      replyToEmail: account.replyToEmail || '',
      priority: account.priority,
      isActive: account.isActive,
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAccount(null);
    setFormData({
      smtpProvider: 'custom',
      email: '',
      smtpUsername: '',
      password: '',
      dailyLimit: 500,
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpSecure: false,
      fromName: 'Atootbandan',
      fromEmail: '',
      replyToEmail: '',
      priority: 1,
      isActive: true,
    });
    setShowPassword(false);
  };

  const handleProviderChange = (provider: string) => {
    const providers: Record<string, any> = {
      gmail: {
        smtpHost: 'smtp.gmail.com',
        smtpPort: 587,
        smtpSecure: false,
        dailyLimit: 500
      },
      'amazon-ses': {
        smtpHost: 'email-smtp.us-east-1.amazonaws.com',
        smtpPort: 587,
        smtpSecure: false,
        dailyLimit: 50000
      },
      custom: {
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        dailyLimit: 500
      }
    };

    const config = providers[provider] || providers.custom;
    setFormData({
      ...formData,
      smtpProvider: provider,
      ...config
    });
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testingAccount) return;

    try {
      setSendingTest(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/admin/smtp/test/${testingAccount._id}`,
        { email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('Test email sent successfully! Check your inbox.');
        setShowTestModal(false);
        setTestEmail('');
        setTestingAccount(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handlePriorityUpdate = async (accountId: string, newPriority: number) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API_URL}/admin/smtp/${accountId}`,
        { priority: newPriority },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('Priority updated successfully');
        fetchAccounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update priority');
    }
  };

  const handleResetPriorities = async () => {
    if (!confirm('This will reset all account priorities to sequential order (1, 2, 3...). Continue?')) return;

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/admin/smtp/reset-priorities`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('All priorities reset to sequential order');
        fetchAccounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset priorities');
    }
  };

  const handleStatusToggle = async (accountId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API_URL}/admin/smtp/${accountId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success(`Account ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchAccounts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getUsagePercentage = (usage: number, limit: number) => {
    return Math.min((usage / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl mb-2">SMTP Email Accounts</h2>
          <p className="text-sm text-gray-600">
            Manage email sender accounts with daily limits and automatic rotation
          </p>
        </div>
        <div className="flex gap-2">
          {accounts.length > 0 && (
            <Button
              onClick={handleResetPriorities}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Reset Priorities
            </Button>
          )}
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-rose-500 to-pink-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add SMTP Account
          </Button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg mb-2">No SMTP Accounts</h3>
          <p className="text-gray-600 mb-4">Add your first SMTP account to start sending emails</p>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-rose-500 to-pink-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Account
          </Button>
        </div>
      ) : (
        <>
          {/* Smart Failover Info Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm text-blue-900 mb-1">🚀 Smart Email Failover Active</h4>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Automatic Priority Management:</strong> If an email fails to send using Priority 1 account, 
                  the system automatically tries Priority 2, then 3, and so on. Failed accounts are automatically moved 
                  to the last priority to prevent repeated failures. No emails will be lost!
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
          {accounts.map((account) => {
            const usagePercentage = getUsagePercentage(account.todayUsage, account.dailyLimit);
            
            return (
              <div
                key={account._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${account.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Mail className={`w-6 h-6 ${account.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg">{account.email}</h3>
                        <button
                          onClick={() => handleStatusToggle(account._id, account.isActive)}
                          className={`px-2 py-1 text-xs rounded-full transition-colors ${
                            account.isActive 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {account.isActive ? 'Active' : 'Inactive'}
                        </button>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-600">Priority:</span>
                          <select
                            value={account.priority}
                            onChange={(e) => handlePriorityUpdate(account._id, parseInt(e.target.value))}
                            className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full border-none cursor-pointer hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        {account.smtpHost}:{account.smtpPort} • From: {account.fromName}
                        {account.fromEmail && <> • Display: {account.fromEmail}</>}
                        {account.replyToEmail && <> • Reply: {account.replyToEmail}</>}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(account)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTestingAccount(account);
                        setTestEmail('');
                        setShowTestModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(account._id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-600">Today's Usage</span>
                    </div>
                    <p className="text-2xl">
                      {account.todayUsage} / {account.dailyLimit}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {account.remainingToday} remaining
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Total Sent</span>
                    </div>
                    <p className="text-2xl">{account.totalEmailsSent.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">All time</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      <span className="text-sm text-gray-600">Status</span>
                    </div>
                    <p className="text-lg">
                      {account.remainingToday > 0 ? (
                        <span className="text-green-600">Available</span>
                      ) : (
                        <span className="text-red-600">Limit Reached</span>
                      )}
                    </p>
                    {account.lastUsedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last used: {new Date(account.lastUsedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Daily Limit Progress</span>
                    <span className={`${usagePercentage >= 90 ? 'text-red-600' : usagePercentage >= 70 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getUsageColor(usagePercentage)}`}
                      style={{ width: `${usagePercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl">
                {editingAccount ? 'Edit SMTP Account' : 'Add SMTP Account'}
              </h3>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* SMTP Provider Selection */}
              <div>
                <label className="block text-sm mb-2">SMTP Provider *</label>
                <select
                  value={formData.smtpProvider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                >
                  <option value="gmail">Gmail</option>
                  <option value="amazon-ses">Amazon SES</option>
                  <option value="custom">Custom SMTP</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a provider for preset configurations
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="sender@gmail.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.smtpProvider === 'gmail' ? 'Your Gmail address' : 'Email address for sending'}
                  </p>
                </div>

                {formData.smtpProvider === 'amazon-ses' && (
                  <div>
                    <label className="block text-sm mb-2">SMTP Username *</label>
                    <input
                      type="text"
                      required
                      value={formData.smtpUsername}
                      onChange={(e) => setFormData({ ...formData, smtpUsername: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your AWS SES SMTP username (different from IAM username)
                    </p>
                  </div>
                )}

                <div className={formData.smtpProvider !== 'amazon-ses' ? 'md:col-span-1' : ''}>
                  <label className="block text-sm mb-2">
                    Password {editingAccount && '(leave empty to keep current)'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required={!editingAccount}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder={formData.smtpProvider === 'gmail' ? 'App password' : 'SMTP password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.smtpProvider === 'amazon-ses' 
                      ? 'Your AWS SES SMTP password' 
                      : 'App password or SMTP password'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Daily Limit *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.dailyLimit}
                    onChange={(e) => setFormData({ ...formData, dailyLimit: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">From Name</label>
                  <input
                    type="text"
                    value={formData.fromName}
                    onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="Atootbandan"
                  />
                </div>
                
                <div>
                  <label className="block text-sm mb-2">From Email (Alias)</label>
                  <input
                    type="email"
                    value={formData.fromEmail}
                    onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="puneet@atootbandan.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Display this email as sender (set up alias in your email provider)
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm mb-2">Reply To Email</label>
                  <input
                    type="email"
                    value={formData.replyToEmail}
                    onChange={(e) => setFormData({ ...formData, replyToEmail: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="support@atootbandan.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Set a reply-to email address
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={formData.smtpHost}
                    onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">SMTP Port</label>
                  <input
                    type="number"
                    value={formData.smtpPort}
                    onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">Priority</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.smtpSecure}
                    onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                    className="w-4 h-4 text-rose-500"
                  />
                  <span className="text-sm">Use Secure Connection (SSL/TLS)</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-rose-500"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm mb-2">Gmail App Password Setup:</h4>
                <ol className="text-xs text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Enable 2-Step Verification in Google Account</li>
                  <li>Go to App Passwords section</li>
                  <li>Generate new app password for "Mail"</li>
                  <li>Copy the 16-character password and paste above</li>
                </ol>
              </div>

              {formData.smtpProvider === 'amazon-ses' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm mb-2">Amazon SES Setup:</h4>
                  <ol className="text-xs text-gray-700 space-y-1 list-decimal list-inside">
                    <li>Go to AWS Console → Amazon SES</li>
                    <li>Verify your domain or email address</li>
                    <li>Navigate to SMTP Settings</li>
                    <li>Click "Create SMTP Credentials"</li>
                    <li>Copy the SMTP Username and SMTP Password provided (not the same as IAM credentials)</li>
                    <li>Choose your region's SMTP endpoint (e.g., email-smtp.us-east-1.amazonaws.com)</li>
                    <li>Daily limit is 50,000 for most accounts (check your SES sending limits)</li>
                  </ol>
                  <p className="text-xs text-purple-700 mt-2">⚠️ Make sure your SES account is out of sandbox mode for production use</p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm mb-2">To Use "From Email" (Alias):</h4>
                <ol className="text-xs text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Go to Gmail Settings → Accounts and Import</li>
                  <li>Click "Add another email address" under "Send mail as"</li>
                  <li>Add your alias email (e.g., puneet@atootbandan.com)</li>
                  <li>Verify the alias by clicking the link sent to that email</li>
                  <li>Gmail will then allow sending from that address</li>
                </ol>
                <p className="text-xs text-amber-700 mt-2">⚠️ Without this setup, Gmail will ignore the From Email field</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {editingAccount ? 'Update Account' : 'Add Account'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl">
                Test SMTP Account
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTestModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!testingAccount) return;
              setSendingTest(true);
              try {
                const token = localStorage.getItem('token');
                const { data } = await axios.post(
                  `${API_URL}/admin/smtp/test/${testingAccount._id}`,
                  { email: testEmail },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                if (data.success) {
                  toast.success('Test email sent successfully');
                }
              } catch (error: any) {
                toast.error(error.response?.data?.message || 'Failed to send test email');
              } finally {
                setSendingTest(false);
              }
            }} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="recipient@example.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingTest ? 'Sending...' : 'Send Test Email'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTestModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}